import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BarChart3, TrendingUp, DollarSign, Ticket, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReportsPage() {
  const { getAuthHeader } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reports`, getAuthHeader());
      setReport(response.data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Reports">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-white rounded border border-border animate-pulse" />
          ))}
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Reports">
      <p className="text-muted-foreground mb-6">Revenue and attendance analytics</p>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="border border-border bg-white">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-1">Total Events</p>
                <p className="font-heading text-3xl font-bold tracking-tight">{report?.total_events || 0}</p>
              </div>
              <div className="bg-[#0055FF] w-10 h-10 rounded flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border bg-white">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-1">Tickets Sold</p>
                <p className="font-heading text-3xl font-bold tracking-tight">{report?.total_tickets_sold || 0}</p>
              </div>
              <div className="bg-[#00C853] w-10 h-10 rounded flex items-center justify-center">
                <Ticket className="w-5 h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border bg-white">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-1">Total Revenue</p>
                <p className="font-heading text-3xl font-bold tracking-tight">D{report?.total_revenue?.toLocaleString() || 0}</p>
              </div>
              <div className="bg-[#FFB300] w-10 h-10 rounded flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border bg-white">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-1">Attendance Rate</p>
                <p className="font-heading text-3xl font-bold tracking-tight">
                  {report?.total_tickets_sold > 0 
                    ? Math.round((report.tickets_used / report.total_tickets_sold) * 100) 
                    : 0}%
                </p>
              </div>
              <div className="bg-[#9C27B0] w-10 h-10 rounded flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ticket Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="border border-border bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#0055FF]" />
              Ticket Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded bg-[#00C853]"></div>
                  <span>Used Tickets</span>
                </div>
                <span className="font-heading text-2xl font-bold">{report?.tickets_used || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded bg-[#FFB300]"></div>
                  <span>Unused Tickets</span>
                </div>
                <span className="font-heading text-2xl font-bold">{report?.tickets_unused || 0}</span>
              </div>
            </div>

            {/* Progress Bar */}
            {report?.total_tickets_sold > 0 && (
              <div className="mt-6">
                <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#00C853]" 
                    style={{ width: `${(report.tickets_used / report.total_tickets_sold) * 100}%` }}
                  ></div>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {Math.round((report.tickets_used / report.total_tickets_sold) * 100)}% of tickets have been used
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-border bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Revenue Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-6">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Total Revenue</p>
              <p className="font-heading text-5xl font-bold tracking-tight text-[#00C853]">
                D{report?.total_revenue?.toLocaleString() || 0}
              </p>
              <p className="text-muted-foreground mt-2">Gambian Dalasi</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Event Breakdown Table */}
      <Card className="border border-border bg-white">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Event Performance</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {report?.events?.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold text-xs uppercase tracking-wide">Event Name</TableHead>
                    <TableHead className="font-semibold text-xs uppercase tracking-wide">Date</TableHead>
                    <TableHead className="font-semibold text-xs uppercase tracking-wide text-center">Tickets Sold</TableHead>
                    <TableHead className="font-semibold text-xs uppercase tracking-wide text-center">Tickets Used</TableHead>
                    <TableHead className="font-semibold text-xs uppercase tracking-wide text-center">Attendance</TableHead>
                    <TableHead className="font-semibold text-xs uppercase tracking-wide text-right">Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.events.map((event) => (
                    <TableRow key={event.id}>
                      <TableCell className="font-semibold">{event.name}</TableCell>
                      <TableCell>{event.date}</TableCell>
                      <TableCell className="text-center">{event.tickets_sold}</TableCell>
                      <TableCell className="text-center">{event.tickets_used}</TableCell>
                      <TableCell className="text-center">
                        {event.tickets_sold > 0 
                          ? `${Math.round((event.tickets_used / event.tickets_sold) * 100)}%`
                          : '0%'
                        }
                      </TableCell>
                      <TableCell className="text-right font-semibold text-[#00C853]">
                        D{event.revenue.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-12 text-center">
              <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No event data available yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
}
